import React, {useEffect, useState} from 'react';
import Table from 'react-bootstrap/Table';
import {FaAngleDown, FaAngleUp, FaTrash} from 'react-icons/fa';
import {PosTransactionChangeTypeEnum, RowStateEnum, TransactionItemResponseModel} from "../../../models";
import {getLabelName, isArabicCurrentLanguage} from "../../../utils";
import {
    accordionLabelStyle,
    accordionRowStyle,
    iconBtnStyle,
    popupActionsStyle,
    popupBoxStyle,
    popupBtnStyle,
    popupCancelBtnStyle,
    popupContainerStyle,
    popupDelBtnStyle,
    popupInputValueStyle,
    popupOkBtnStyle,
    popupRowStyle,
    qtyBtnStyle,
    qtyInputStyle,
    quantityCellStyle,
    tableHeadColoredStyle,
    tableHeadEmptyStyle,
    tableItemCellStyle,
} from "../common/css/posCss";


interface PosTransactionItemsTableProps  {
    data: TransactionItemResponseModel[];
    onUpdateItem?: (updatedItem: TransactionItemResponseModel,  changeType: PosTransactionChangeTypeEnum) => void;
}

export const PosTransactionItemsTable: React.FC<PosTransactionItemsTableProps> = ({ data, onUpdateItem }) => {
    //#region state
    const [isMobile, setIsMobile] = useState(window.innerWidth < 800);
    const [isNarrow, setIsNarrow] = useState(window.innerWidth < 580);
    const [expandedRow, setExpandedRow] = useState<string | null>(null);
    const [popupRowKey, setPopupRowKey] = useState<string | null>(null);
    const [inputValue, setInputValue] = useState<string>('');
    //#endregion
    //#region useEffect
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 800);
            setIsNarrow(window.innerWidth < 580);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    //#endregion
    //#region functions
    const toggleAccordion = (rowKey: string) => {
        setExpandedRow(expandedRow === rowKey ? null : rowKey);
    };
    const handleDelete = (row: TransactionItemResponseModel) => {
        onUpdateItem?.({ ...row, rowState: RowStateEnum.Delete },PosTransactionChangeTypeEnum.ItemDeleted);
    };
    const handleQuantityChange = (
        row: TransactionItemResponseModel,
        action: 'increment' | 'decrement'
    ) => {
        const newQuantity = action === 'increment' ? row.Quantity + 1 : Math.max(1, row.Quantity - 1);
        onUpdateItem?.({ ...row, Quantity: newQuantity },PosTransactionChangeTypeEnum.QuantityChanged);
    };
    //#endregion
    return (
        <Table bordered className="text-center align-middle">
            <thead>
            <tr className="custom-header">
                <th style={tableHeadEmptyStyle}></th>
                <th style={tableHeadColoredStyle}>{getLabelName("Item")}</th>
                <th style={tableHeadColoredStyle}>{getLabelName("Quantity")}</th>
                <th style={tableHeadColoredStyle}>{getLabelName("Total")}</th>
                <th style={tableHeadEmptyStyle}></th>
            </tr>
            </thead>
            <tbody>
            {data.map((row, index) => (
                <React.Fragment key={`${row.rowKey}-${index}`}>
                    <tr>
                        <td>
                            <button style={iconBtnStyle} onClick={() => handleDelete(row)}>
                                <FaTrash className="text-danger" />
                            </button>
                        </td>
                        <td style={tableItemCellStyle}>
                            {isArabicCurrentLanguage() ? row.ItemName : row.ItemName_En}
                        </td>
                        <td>
                            <div style={quantityCellStyle}>
                                {isNarrow && <button style={qtyBtnStyle} onClick={() => handleQuantityChange(row, 'decrement')}>-</button>}
                                <input
                                    value={row.Quantity}
                                    readOnly
                                    style={qtyInputStyle}
                                    onClick={() => {
                                        setPopupRowKey(row.rowKey);
                                        setInputValue(row.Quantity.toString());
                                    }}
                                />
                                {isNarrow && <button style={qtyBtnStyle} onClick={() => handleQuantityChange(row, 'increment')}>+</button>}
                            </div>
                        </td>
                        <td>{row.Total.toFixed(2)}</td>
                        <td>
                            <button style={iconBtnStyle} onClick={() => toggleAccordion(index.toString())}>
                                {expandedRow === index.toString() ? <FaAngleUp /> : <FaAngleDown />}
                            </button>
                        </td>
                    </tr>
                    {expandedRow === index.toString() && (
                        <tr>
                            <td colSpan={5} style={accordionRowStyle}>
                                <div>
                                    <p style={accordionLabelStyle}>{getLabelName("Price")}:<span>{row.Unit_PriceInCurrencyCalculated} </span></p>
                                    <p style={accordionLabelStyle}>{getLabelName("Price with tax")}:<span>{row.UnitPriceWithTaxCalculated} </span></p>
                                    <p style={accordionLabelStyle}>{getLabelName("Discount")}:<span>{row.ItemDiscount} </span></p>
                                </div>
                            </td>
                        </tr>
                    )}
                    {!isNarrow && popupRowKey === row.rowKey && (
                        <tr>
                            <td colSpan={5} style={popupRowStyle}>
                                <div style={popupContainerStyle}>
                                    <div style={{ ...popupBoxStyle, width: isMobile ? '300px' : '200px' }}>
                                        <div style={popupInputValueStyle}>{inputValue}</div>
                                        {Array.from({ length: 10 }, (_, i) => i).map((num) => (
                                            <button
                                                key={num}
                                                onClick={() => setInputValue((prev) => prev + num.toString())}
                                                style={popupBtnStyle}
                                            >
                                                {num}
                                            </button>
                                        ))}
                                        <button style={popupDelBtnStyle} onClick={() => setInputValue((prev) => prev.slice(0, -1))}>DEL</button>
                                        <div style={popupActionsStyle}>
                                            <button
                                                style={popupCancelBtnStyle}
                                                onClick={() => {
                                                    setPopupRowKey(null);
                                                    setInputValue('');
                                                }}
                                            >Cancel</button>
                                            <button
                                                style={popupOkBtnStyle}
                                                onClick={() => {
                                                    const updatedQuantity = parseInt(inputValue || '0', 10);
                                                    onUpdateItem?.({
                                                        ...row,
                                                        Quantity: updatedQuantity,
                                                    },PosTransactionChangeTypeEnum.QuantityChanged);
                                                    setPopupRowKey(null);
                                                    setInputValue('');
                                                }}
                                            >OK</button>
                                        </div>
                                    </div>
                                </div>
                            </td>
                        </tr>
                    )}
                </React.Fragment>
            ))}
            </tbody>
        </Table>
    );
};

//export default PosTransactionItemsTable;
